import React, { useState } from 'react';
import { Check, FileText, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const BAAAcceptance = ({ onAccept, userEmail }) => {
  const [isAccepted, setIsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAcceptance = async () => {
    if (!isAccepted) {
      toast.error('Please check the box to accept the Business Associate Agreement');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAccept();
      toast.success('BAA accepted successfully');
    } catch (error) {
      console.error('Error accepting BAA:', error);
      toast.error('Failed to save BAA acceptance. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center">
            <Shield className="w-8 h-8 mr-3" />
            <div>
              <h1 className="text-2xl font-bold">Business Associate Agreement Required</h1>
              <p className="text-blue-100 mt-1">Please review and accept our BAA to continue</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900">HIPAA Compliance Required</h3>
                  <p className="text-blue-700 text-sm mt-1">
                    As a healthcare professional, you must accept our Business Associate Agreement
                    before accessing Chartara Notes to ensure HIPAA compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* BAA Content */}
          <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto border">
            <div className="prose max-w-none text-sm">
              <h2 className="text-xl font-bold text-center mb-4">
                BUSINESS ASSOCIATE AGREEMENT - CHARTARA LABS
              </h2>
              <p className="text-center mb-6"><strong>Effective Date: September 17th, 2025</strong></p>

              <p className="mb-4">
                This Business Associate Agreement ("Agreement") is entered into between you, the healthcare
                professional ("Covered Entity"), and Chartara Labs ("Business Associate").
              </p>

              <h3 className="text-lg font-semibold mt-6 mb-3">1. DEFINITIONS</h3>
              <p className="mb-3">
                Terms used in this Agreement shall have the meanings assigned to them in the HIPAA Privacy
                Rule (45 CFR Parts 160 and 164).
              </p>
              <ul className="list-disc ml-6 mb-4">
                <li>"Protected Health Information" (PHI) means individually identifiable health information transmitted or maintained by Business Associate on behalf of Covered Entity</li>
                <li>"Required by Law" means a mandate contained in law that compels an entity to make a use or disclosure of PHI</li>
                <li>"Secretary" means the Secretary of the Department of Health and Human Services or designee</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">2. PERMITTED USES AND DISCLOSURES</h3>
              <p className="mb-3">Business Associate may use and disclose PHI only to:</p>
              <ul className="list-disc ml-6 mb-3">
                <li>Provide the voice documentation platform services as specified in the Terms of Service</li>
                <li>Perform functions, activities, or services as specified in our service agreement</li>
                <li>Comply with legal requirements</li>
              </ul>
              <p className="mb-3">Business Associate may NOT:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Use or disclose PHI for any purpose other than providing our platform services</li>
                <li>Use PHI for Business Associate's own marketing purposes</li>
                <li>Sell PHI or receive remuneration in exchange for PHI</li>
                <li>Combine PHI with other information except as permitted by this Agreement</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">3. SAFEGUARDS</h3>
              <p className="mb-3">Business Associate agrees to:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Implement appropriate administrative, physical, and technical safeguards to protect PHI</li>
                <li>Ensure PHI is encrypted both in transit and at rest</li>
                <li>Maintain access controls limiting PHI access to authorized personnel only</li>
                <li>Conduct regular security assessments and updates</li>
                <li>Train all workforce members on HIPAA compliance requirements</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">4. REPORTING REQUIREMENTS</h3>
              <p className="mb-3">Business Associate will:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Report any suspected or actual breach of PHI within 24 hours of discovery</li>
                <li>Provide all information necessary for Covered Entity to meet breach notification requirements</li>
                <li>Assist in any breach investigation and mitigation efforts</li>
                <li>Maintain documentation of all security incidents</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">5. ACCESS AND AMENDMENT RIGHTS</h3>
              <p className="mb-3">Business Associate agrees to:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Provide access to PHI when requested by Covered Entity for individual access requests</li>
                <li>Make amendments to PHI as directed by Covered Entity</li>
                <li>Maintain logs of all PHI access and modifications</li>
                <li>Provide accounting of disclosures when requested</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">6. MINIMUM NECESSARY</h3>
              <p className="mb-3">Business Associate will:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Limit uses and disclosures of PHI to the minimum necessary to accomplish the intended purpose</li>
                <li>Request only the minimum amount of PHI necessary to provide services</li>
                <li>Implement policies limiting access to PHI based on job responsibilities</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">7. SUBCONTRACTORS</h3>
              <p className="mb-3">If Business Associate uses subcontractors that will have access to PHI:</p>
              <ul className="list-disc ml-6 mb-3">
                <li>Business Associate will ensure subcontractors agree to the same restrictions as this Agreement</li>
                <li>All subcontractors will sign appropriate Business Associate Agreements</li>
                <li>Business Associate remains liable for subcontractor compliance</li>
              </ul>
              <p className="mb-3"><strong>Current HIPAA-Compliant Subcontractors:</strong></p>
              <ul className="list-disc ml-6 mb-4">
                <li>Amazon Web Services (AWS) - Cloud hosting and data storage</li>
                <li>Microsoft Azure - AI services for speech-to-text and note generation</li>
                <li>[Additional subcontractors will be disclosed as added]</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">8. TERMINATION</h3>
              <p className="mb-3">This Agreement terminates when:</p>
              <ul className="list-disc ml-6 mb-3">
                <li>The Terms of Service agreement is terminated</li>
                <li>Either party provides 30 days written notice</li>
                <li>There is a material breach that cannot be cured within 30 days</li>
              </ul>
              <p className="mb-3">Upon termination:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Business Associate will return or destroy all PHI within 30 days</li>
                <li>If return or destruction is not feasible, Business Associate will extend protections indefinitely</li>
                <li>Business Associate will provide certification of destruction or continued protection</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">9. BREACH CONSEQUENCES</h3>
              <p className="mb-3">If Business Associate breaches this Agreement:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Covered Entity may immediately terminate this Agreement</li>
                <li>Business Associate will bear all costs associated with breach notification and remediation</li>
                <li>Business Associate will cooperate fully with any regulatory investigations</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">10. COMPLIANCE CERTIFICATION</h3>
              <p className="mb-3">Business Associate certifies that:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>All workforce members are trained on HIPAA requirements</li>
                <li>Appropriate technical safeguards are implemented and maintained</li>
                <li>Business Associate maintains cyber liability insurance</li>
                <li>Regular risk assessments are conducted and documented</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">11. REGULATORY CHANGES</h3>
              <p className="mb-4">
                This Agreement will be amended as necessary to comply with changes in HIPAA regulations
                or other applicable privacy laws. Business Associate will notify Covered Entity of any
                required changes within 30 days.
              </p>

              <h3 className="text-lg font-semibold mt-6 mb-3">12. INDIVIDUAL RIGHTS</h3>
              <p className="mb-3">Business Associate acknowledges that individuals have rights under HIPAA including:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>Right to access their PHI</li>
                <li>Right to request amendments</li>
                <li>Right to request restrictions on use/disclosure</li>
                <li>Right to file complaints</li>
              </ul>
              <p className="mb-4">
                Business Associate will assist Covered Entity in meeting these individual rights requests.
              </p>

              <h3 className="text-lg font-semibold mt-6 mb-3">13. CONTACT INFORMATION</h3>
              <p className="mb-4">
                For Business Associate Agreement matters:<br />
                Email: support@chartaralabs.io<br />
                Phone: 315-278-1649
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <p className="text-blue-900 font-semibold text-center">
                  BY USING CHARTARA NOTES SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD,
                  AND AGREE TO BE BOUND BY THIS BUSINESS ASSOCIATE AGREEMENT.
                </p>
              </div>
            </div>
          </div>

          {/* Acceptance Section */}
          <div className="mt-6 border-t pt-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="baa-acceptance"
                  checked={isAccepted}
                  onChange={(e) => setIsAccepted(e.target.checked)}
                  className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="baa-acceptance" className="ml-3 text-sm font-medium text-gray-900">
                  I have read, understood, and agree to be bound by this Business Associate Agreement
                </label>
              </div>

              <div className="text-xs text-gray-600 mb-4">
                <p><strong>User Email:</strong> {userEmail}</p>
                <p><strong>Acceptance Date:</strong> {new Date().toLocaleDateString()}</p>
              </div>

              <button
                onClick={handleAcceptance}
                disabled={!isAccepted || isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center ${
                  isAccepted && !isSubmitting
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Accept BAA and Continue to Chartara Notes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BAAAcceptance;